// Platform-admin context provider — task 130 D6.
//
// Probes `/api/v1/platform/tenants?limit=1` on mount. The endpoint is gated
// by the platform-admin plugin (task 130 D2) which returns 404 to non-
// admins; a 200 response means the user is a platform admin.
//
// - Status `unknown` while the probe is in flight — the route guard must
//   render nothing (or a bare <Suspense>-style placeholder) so non-admins
//   never see platform chrome before the 404 redirect.
// - Status `admin` on 200. Cached for the session — no polling, no refetch.
// - Status `not_admin` on 404 (or any other error). The route guard
//   redirects to `/`.
//
// Tripwire: this provider speaks to one endpoint and caches a boolean. If
// a platform-admin write surface ever ships, it must migrate identity to a
// DB-backed grant record; this probe will still work, but the gate
// identity lives in the DB. See ADR 0033.

import { createContext, useContext, useEffect, useRef, useState, type ReactNode } from 'react';
import { useAppContext } from '@/context/AppContext';
import { apiFetch, ApiError } from '@/lib/api';

export type PlatformAdminStatus = 'unknown' | 'admin' | 'not_admin';

interface PlatformAdminContextValue {
  status: PlatformAdminStatus;
}

const PlatformAdminContext = createContext<PlatformAdminContextValue | null>(null);

// eslint-disable-next-line react-refresh/only-export-components -- hook colocated with provider is the idiomatic React context pattern
export function usePlatformAdminStatus(): PlatformAdminStatus {
  const ctx = useContext(PlatformAdminContext);
  return ctx?.status ?? 'unknown';
}

export function PlatformAdminProvider({ children }: { children: ReactNode }) {
  const { mockupMode, getToken, tokenResolverReady } = useAppContext();
  // Initialize mockup mode synchronously in useState — mockupMode never
  // flips to a mid-session live probe, so this doesn't need to be an
  // effect (avoids the setState-in-effect warning).
  const [status, setStatus] = useState<PlatformAdminStatus>(mockupMode ? 'admin' : 'unknown');
  const probedRef = useRef(false);

  useEffect(() => {
    if (probedRef.current) return;
    // Mockup short-circuit MUST run before the tokenResolverReady gate:
    // ClerkSync never mounts in mockup mode, so tokenResolverReady stays
    // false there. Without this ordering the mockup-mode admin would
    // never get past the early-return below. Task 148.
    if (mockupMode) return;
    // Wait for ClerkSync's setGetToken to install the real resolver.
    // Without this gate, child-effect-before-parent-effect ordering on
    // first commit fires the probe with `noopToken`, the request goes
    // out unauthenticated, the backend 401s, and the probedRef latch
    // pins the user as 'not_admin' for the lifetime of the provider.
    // Task 148.
    if (!tokenResolverReady) return;
    probedRef.current = true;

    let cancelled = false;
    (async () => {
      try {
        const token = await getToken();
        await apiFetch('/api/v1/platform/tenants?limit=1', token);
        if (!cancelled) setStatus('admin');
      } catch (err) {
        if (cancelled) return;
        if (err instanceof ApiError && err.status === 404) {
          setStatus('not_admin');
        } else {
          // Network failure, 500, etc. — treat as not admin so we never
          // render platform chrome on an indeterminate result.
          setStatus('not_admin');
        }
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [mockupMode, getToken, tokenResolverReady]);

  return (
    <PlatformAdminContext.Provider value={{ status }}>{children}</PlatformAdminContext.Provider>
  );
}
